<template>
  <AdminLayout>
    <template #topbar-search>
      <div v-if="currentView === 'Summary'" class="flex flex-column">
        <IconField>
          <InputIcon class="pi pi-search" />
          <InputText v-model="searchValue" placeholder="Search by tutor username..." size="small" @keyup.enter="applySummaryFilters" />
        </IconField>
      </div>
    </template>

    <template #header>
      <Breadcrumb :items="items" />
    </template>

    <div class="flex flex-column md:flex-row justify-content-between align-items-start md:align-items-start mb-4 mt-2 gap-3">

      <div class="back-section w-full md:w-auto">
        <Button v-if="currentView !== 'Summary'" icon="pi pi-arrow-left" label="Back to Tutor Payouts" class="p-button-text Caption back-button p-0" @click="goBack" />
      </div>

      <div class="w-full md:w-auto flex flex-column align-items-start md:align-items-end gap-2">

        <!-- Summary Filters -->
        <div v-if="currentView === 'Summary'" class="filter-row">
          <div class="filter-date-wrap">
            <Calendar
              v-model="summaryFilters.startDate"
              placeholder="Start Date"
              size="small"
              dateFormat="yy-mm-dd"
              showIcon
              class="filter-calendar"
              :class="{ 'p-invalid': hasSummaryDateError }"
            />
          </div>
          <div class="filter-date-wrap">
            <Calendar
              v-model="summaryFilters.endDate"
              placeholder="End Date"
              size="small"
              dateFormat="yy-mm-dd"
              showIcon
              class="filter-calendar"
              :class="{ 'p-invalid': hasSummaryDateError }"
            />
          </div>
          <div class="filter-actions">
            <Button label="Apply" class="accent-button" @click="applySummaryFilters" :disabled="hasSummaryDateError" />
            <Button icon="pi pi-filter-slash" class="accent-button-transparent p-button-icon-only" @click="clearSummaryFilters" aria-label="Clear Filters" />
          </div>
        </div>

        <!-- History Filters -->
        <div v-if="currentView === 'History'" class="filter-row">
          <div class="filter-date-wrap">
            <Calendar
              v-model="historyFilters.startDate"
              placeholder="Start Date"
              size="small"
              dateFormat="yy-mm-dd"
              showIcon
              class="filter-calendar"
              :class="{ 'p-invalid': hasHistoryDateError }"
            />
          </div>
          <div class="filter-date-wrap">
            <Calendar
              v-model="historyFilters.endDate"
              placeholder="End Date"
              size="small"
              dateFormat="yy-mm-dd"
              showIcon
              class="filter-calendar"
              :class="{ 'p-invalid': hasHistoryDateError }"
            />
          </div>
          <div class="filter-actions">
            <Button label="Apply" class="accent-button" @click="applyHistoryFilters" :disabled="hasHistoryDateError" />
            <Button icon="pi pi-filter-slash" class="accent-button-transparent p-button-icon-only" @click="clearHistoryFilters" aria-label="Clear Filters" />
          </div>
        </div>

        <small v-if="currentView === 'Summary' && hasSummaryDateError" class="p-error w-full text-left">Start date cannot be after end date.</small>
        <small v-if="currentView === 'History' && hasHistoryDateError" class="p-error w-full text-left">Start date cannot be after end date.</small>
      </div>

    </div>

    <div class="container">
      <PayoutSummaryTable
        v-if="currentView === 'Summary'"
        :tableData="summaryData"
        :isLoading="isLoading"
        :rowsPerPage="rowsPerPage"
        :totalRecords="totalRecords"
        :currentPage="currentPage"
        @page-change="onSummaryPageChange"
        @row-click="viewTutorHistory"
      />

      <PaymentHistoryTable
        v-if="currentView === 'History'"
        :tableData="historyData"
        :isLoading="isLoading"
        :rowsPerPage="historyRowsPerPage"
        :totalRecords="historyTotalRecords"
        :currentPage="historyCurrentPage"
        @page-change="onHistoryPageChange"
      />
    </div>
  </AdminLayout>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import AdminLayout from "@/layout/AdminLayout.vue";
import PayoutSummaryTable from "@/components/dashboard/admin/tutorPayouts/payoutSummaryTable.vue";
import PaymentHistoryTable from "@/components/dashboard/admin/tutorPayouts/paymentHistoryTable.vue";
import Breadcrumb from "@/components/CustomBreadcrumb.vue";
import Button from "primevue/button";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import InputText from "primevue/inputtext";
import Calendar from "primevue/calendar";
import { getTutorPayoutSummary, getTutorPaymentHistory } from "@/api/admin";
import { useToast } from "primevue/usetoast";

const toast = useToast();
const currentView = ref("Summary");
const isLoading = ref(false);

// --- Utility: Format Date for API (YYYY-MM-DD) ---
const formatDateForAPI = (dateStr) => {
  if (!dateStr) return undefined;
  const d = new Date(dateStr);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().split("T")[0];
};

// --- Summary State & Filters ---
const summaryData = ref([]);
const totalRecords = ref(0);
const rowsPerPage = ref(10);
const currentPage = ref(1);
const searchValue = ref("");

const summaryFilters = ref({
  startDate: null,
  endDate: null,
});

// --- History State & Filters ---
const selectedTutor = ref(null);
const historyData = ref([]);
const historyTotalRecords = ref(0);
const historyRowsPerPage = ref(10);
const historyCurrentPage = ref(1);

const historyFilters = ref({
  startDate: null,
  endDate: null,
});

// --- Validation ---
const hasSummaryDateError = computed(() => {
  const { startDate, endDate } = summaryFilters.value;
  return startDate && endDate && startDate > endDate;
});

const hasHistoryDateError = computed(() => {
  const { startDate, endDate } = historyFilters.value;
  return startDate && endDate && startDate > endDate;
});

// --- Fetch API Methods ---
const fetchSummary = async () => {
  try {
    isLoading.value = true;
    const response = await getTutorPayoutSummary({
      page: currentPage.value,
      limit: rowsPerPage.value,
      searchTerm: searchValue.value || undefined,
      startDate: formatDateForAPI(summaryFilters.value.startDate),
      endDate: formatDateForAPI(summaryFilters.value.endDate),
    });
    summaryData.value = response.data.data.tutors;
    totalRecords.value = response.data.data.pagination.totalRecords;
  } catch (error) {
    console.error("Failed to fetch payout summary:", error);
    toast.add({ severity: "error", summary: "Error", detail: "Failed to load summaries", life: 3000 });
  } finally {
    isLoading.value = false;
  }
};

const fetchHistory = async () => {
  if (!selectedTutor.value) return;
  try {
    isLoading.value = true;
    const response = await getTutorPaymentHistory(selectedTutor.value._id, {
      page: historyCurrentPage.value,
      limit: historyRowsPerPage.value,
      startDate: formatDateForAPI(historyFilters.value.startDate),
      endDate: formatDateForAPI(historyFilters.value.endDate),
    });
    historyData.value = response.data.data.payments;
    historyTotalRecords.value = response.data.data.pagination.totalRecords;
  } catch (error) {
    console.error("Failed to fetch payment history:", error);
    toast.add({ severity: "error", summary: "Error", detail: "Failed to load history", life: 3000 });
  } finally {
    isLoading.value = false;
  }
};

// --- Filter Action Methods ---
const applySummaryFilters = () => {
  if (hasSummaryDateError.value) return;
  currentPage.value = 1;
  fetchSummary();
};

const clearSummaryFilters = () => {
  summaryFilters.value = { startDate: null, endDate: null };
  searchValue.value = "";
  applySummaryFilters();
};

const applyHistoryFilters = () => {
  if (hasHistoryDateError.value) return;
  historyCurrentPage.value = 1;
  fetchHistory();
};

const clearHistoryFilters = () => {
  historyFilters.value = { startDate: null, endDate: null };
  applyHistoryFilters();
};

watch(searchValue, () => {
  currentPage.value = 1;
  fetchSummary();
});

onMounted(fetchSummary);

// --- Navigation & Breadcrumbs ---
const items = computed(() => {
  const breadcrumbItems = [{ label: "Tutor Payouts" }];
  if (currentView.value === "History" && selectedTutor.value) {
    breadcrumbItems.push({ label: `${selectedTutor.value.username}'s History` });
  }
  return breadcrumbItems;
});

const onSummaryPageChange = ({ page, limit }) => {
  currentPage.value = page;
  rowsPerPage.value = limit;
  fetchSummary();
};

const onHistoryPageChange = ({ page, limit }) => {
  historyCurrentPage.value = page;
  historyRowsPerPage.value = limit;
  fetchHistory();
};

const viewTutorHistory = (tutor) => {
  selectedTutor.value = tutor;
  historyCurrentPage.value = 1;
  clearHistoryFilters();
  currentView.value = "History";
};

const goBack = () => {
  selectedTutor.value = null;
  historyData.value = [];
  currentView.value = "Summary";
  fetchSummary();
};
</script>

<style scoped>
.container {
  width: 100%;
  height: auto;
  border-radius: 8px;
}

.back-section {
  display: flex;
  align-items: center;
}

.back-button {
  color: var(--gray-600) !important;
  font-size: 12px !important;
}

.back-button:hover {
  background-color: var(--gray-200) !important;
}

/* ── Filter Row Layout ── */
.filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  width: 100%;
}

/* Each field owns its own flex slot — no sharing, no overriding */
.filter-date-wrap {
  flex: 1 1 160px;
  min-width: 0;
}

.filter-actions {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
  align-items: center;
}

/* ── Force PrimeVue internals to fill their wrapper ── */
:deep(.filter-calendar) {
  width: 100% !important;
  max-width: 100% !important;
}

:deep(.filter-calendar .p-inputtext) {
  width: 100% !important;
  min-width: 0 !important;
  border: 1px solid #d1d5db;
}

:deep(.filter-calendar.p-calendar) {
  width: 100% !important;
}

:deep(.filter-calendar .p-calendar-w-btn) {
  width: 100% !important;
}

:deep(.filter-calendar.p-dropdown) {
  width: 100% !important;
  border: 1px solid #d1d5db;
}

:deep(.filter-calendar .p-button.p-datepicker-trigger) {
  flex-shrink: 0;
  border: 1px solid #d1d5db;
  border-left: none;
}

/* ── Mobile: stack everything full-width ── */
@media (max-width: 767px) {
  .filter-date-wrap {
    flex: 1 1 100%;
  }

  .filter-actions {
    width: 100%;
  }

  .filter-actions .accent-button {
    flex: 1;
  }
}
</style>